import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, FileText, Send, LogOut, Loader2, CheckCircle2, AlertCircle,
  Download, Mail, User, GraduationCap, Calendar, DollarSign,
  RefreshCw, ScrollText, UserCog,
} from "lucide-react";
import { api } from "@/lib/api";
import { getPortalToken, getPortalUser, clearPortalAuth } from "@/lib/portalAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const BASE_TABS = [
  { id: "overview", label: "Overview", Icon: Home },
  { id: "documents", label: "Documents", Icon: FileText },
  { id: "records", label: "Records Request", Icon: Send },
];

function buildTabs(settings) {
  const tabs = [...BASE_TABS];
  if (settings?.feature_student_management) tabs.push({ id: "students", label: "Student Status", Icon: UserCog });
  if (settings?.feature_reenrollment) tabs.push({ id: "reenroll", label: "Re-enrollment", Icon: RefreshCw });
  if (settings?.feature_transcripts) tabs.push({ id: "transcript", label: "Transcript", Icon: ScrollText });
  return tabs;
}

export default function PortalDashboardPage() {
  const nav = useNavigate();
  const token = getPortalToken();
  const stored = getPortalUser();
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    return api.get("/portal/me", { params: { token } })
      .then((r) => setMe(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          clearPortalAuth();
          nav("/portal/login");
        } else {
          toast.error("Failed to load your portal.");
        }
      });
  };

  useEffect(() => {
    if (!token) return;
    reload().finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!token) return <Navigate to="/portal/login" replace />;

  const logout = () => {
    clearPortalAuth();
    toast.success("Signed out.");
    nav("/portal/login");
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-grey-light flex items-center justify-center">
        <Loader2 className="animate-spin text-purple" size={30} />
      </div>
    );
  }

  const isPaid = me.payment_status === "paid";
  const tabs = buildTabs(me.settings);

  return (
    <div className="min-h-screen bg-grey-light flex" data-testid="portal-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-purple-dark text-white flex flex-col shrink-0">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <img src="/zca-logo.png" alt="ZCA" className="h-9 w-9 bg-white rounded-sm p-0.5" />
            <div className="h-serif text-lg text-gold-light leading-tight">Parent Portal</div>
          </div>
          <div className="text-sm text-white/90 truncate font-bold">
            {me.parent_first_name} {me.parent_last_name}
          </div>
          <div className="text-[10px] tracking-widest uppercase text-gold mt-1 font-mono">
            {me.enrollment_id}
          </div>
        </div>
        <nav className="flex-1 py-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`portal-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                tab === t.id
                  ? "bg-purple text-white border-l-4 border-gold"
                  : "text-white/75 hover:bg-white/5 border-l-4 border-transparent"
              }`}
            >
              <t.Icon size={16} />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs text-white/70 hover:text-gold-light px-2 py-2"
            data-testid="portal-view-website-link"
          >
            <Home size={14} /> View website
          </Link>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-xs text-white/70 hover:text-red-300 px-2 py-2 w-full"
            data-testid="portal-logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto">
        {tab === "overview" && <OverviewTab me={me} isPaid={isPaid} />}
        {tab === "documents" && <DocumentsTab me={me} token={token} isPaid={isPaid} />}
        {tab === "records" && <RecordsTab me={me} token={token} />}
        {tab === "students" && <StudentsTab me={me} token={token} onChange={reload} />}
        {tab === "reenroll" && <ReenrollTab me={me} token={token} onChange={reload} />}
        {tab === "transcript" && <TranscriptTab me={me} token={token} />}
      </main>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function OverviewTab({ me, isPaid }) {
  const instr = me.payment_method === "cash_check_now"
    ? me.settings?.payment_instructions_cash
    : me.settings?.payment_instructions_plan;

  return (
    <div className="p-8 max-w-4xl">
      <Header title="Overview" subtitle="Your family's enrollment at a glance." />
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <StatusCard
          label="Payment status"
          value={isPaid ? "Paid — confirmed" : "Awaiting payment"}
          Icon={isPaid ? CheckCircle2 : AlertCircle}
          accent={isPaid ? "emerald" : "amber"}
          testid="status-payment"
        />
        <StatusCard
          label="School year"
          value={me.school_year}
          Icon={Calendar}
          accent="purple"
          testid="status-year"
        />
        <StatusCard
          label="Students enrolled"
          value={(me.students || []).length}
          Icon={GraduationCap}
          accent="purple"
          testid="status-students"
        />
        <StatusCard
          label="Amount"
          value={`$${(me.amount_due || 0).toLocaleString()}`}
          Icon={DollarSign}
          accent="gold"
          testid="status-amount"
        />
      </div>

      {!isPaid && (
        <div className="bg-amber-50 border border-amber-300 p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-700 shrink-0 mt-0.5" size={18} />
            <div>
              <div className="font-bold text-amber-900 mb-1">Next step: submit your payment</div>
              <p className="text-sm text-ink/80 leading-relaxed whitespace-pre-line" data-testid="portal-payment-instructions">
                {instr}
              </p>
              <p className="text-xs text-ink/60 mt-3 italic">
                Once Brady confirms your payment, your enrollment documents
                unlock in the Documents tab and are also emailed to {me.email}.
              </p>
            </div>
          </div>
        </div>
      )}

      <SectionCard title="Family">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <InfoRow label="Parent / Guardian" value={`${me.parent_first_name} ${me.parent_last_name}`} Icon={User} />
          <InfoRow label="Email" value={me.email} Icon={Mail} />
          <InfoRow label="Phone" value={me.phone} />
          <InfoRow
            label="Address"
            value={`${me.address_line1}${me.address_line2 ? `, ${me.address_line2}` : ""}, ${me.city}, ${me.state} ${me.zip_code}`}
          />
        </div>
      </SectionCard>

      <SectionCard title="Students">
        <ul className="divide-y divide-grey">
          {(me.students || []).map((s, i) => (
            <li key={i} className="py-3 flex items-center justify-between" data-testid={`portal-student-${i}`}>
              <div>
                <div className="font-bold text-ink">{s.full_name}</div>
                <div className="text-xs text-ink/60">
                  {s.grade_level} · DOB {s.date_of_birth} · {s.gender}
                </div>
              </div>
              <div className="text-xs tracking-widest uppercase text-purple-light font-bold">
                {s.status || "Active"}
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

/* ---------------- Documents ---------------- */
function DocumentsTab({ me, token, isPaid }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const dl = (doc_type, extra = "") =>
    `${backend}/api/portal/documents/${doc_type}?token=${encodeURIComponent(token)}${extra}`;

  if (!isPaid) {
    return (
      <div className="p-8 max-w-3xl">
        <Header title="Documents" subtitle="Your official enrollment PDFs unlock once your payment is confirmed." />
        <div className="border border-grey bg-white p-10 text-center">
          <AlertCircle className="mx-auto text-amber-600 mb-4" size={40} />
          <div className="h-serif text-2xl text-purple-dark mb-2">Waiting on payment confirmation</div>
          <p className="text-sm text-ink/70 leading-relaxed max-w-lg mx-auto">
            As soon as Director Brady Renfroe records your payment, three
            official documents will appear here and be emailed to{" "}
            <b className="text-purple">{me.email}</b>:
          </p>
          <ul className="text-sm text-ink/80 mt-4 space-y-1 inline-block text-left">
            <li>· Church School Enrollment Form (one per student)</li>
            <li>· Welcome Letter (with dress code and core values)</li>
            <li>· BOE Instructions (to notify your county Board of Education)</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <Header title="Documents" subtitle="Your official enrollment PDFs. Re-download any time." />
      <div className="space-y-3">
        {(me.students || []).map((s, i) => (
          <DocRow
            key={i}
            title={`Church School Enrollment Form — ${s.full_name}`}
            subtitle={`${s.grade_level} · ${me.school_year}`}
            href={dl("enrollment", `&student_index=${i}`)}
            testid={`portal-download-enrollment-${i}`}
          />
        ))}
        <DocRow
          title="Welcome Letter"
          subtitle="Vision, mission, core values, dress code"
          href={dl("welcome")}
          testid="portal-download-welcome"
        />
        <DocRow
          title="BOE Instructions"
          subtitle="How to notify your county Board of Education"
          href={dl("boe")}
          testid="portal-download-boe"
        />
        <DocRow
          title="Family Handbook"
          subtitle="Vision, mission, calendar, tuition & payment, dress code — 2026-2027"
          href={`${backend}/api/handbook`}
          testid="portal-download-handbook"
        />
      </div>
    </div>
  );
}

function DocRow({ title, subtitle, href, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="flex items-center justify-between bg-white border border-grey hover:border-purple p-5 transition-colors group"
    >
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 bg-purple-dark text-gold-light flex items-center justify-center">
          <FileText size={20} />
        </div>
        <div>
          <div className="font-bold text-ink group-hover:text-purple">{title}</div>
          <div className="text-xs text-ink/60 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <div className="text-xs tracking-widest uppercase font-bold text-purple inline-flex items-center gap-1.5">
        <Download size={13} /> PDF
      </div>
    </a>
  );
}

/* ---------------- Records Request ---------------- */
function RecordsTab({ me, token }) {
  const students = me.students || [];
  const [studentIdx, setStudentIdx] = useState("0");
  const [schoolName, setSchoolName] = useState("");
  const [schoolAddress, setSchoolAddress] = useState("");
  const [schoolEmail, setSchoolEmail] = useState("");
  const [grades, setGrades] = useState("");
  const [studentId, setStudentId] = useState("");
  const [sendToSchool, setSendToSchool] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (sendToSchool && !schoolEmail) {
      toast.error("Enter the prior school's email or uncheck 'Also send directly to prior school'.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/portal/records-request",
        {
          student_index: Number(studentIdx),
          prior_school_name: schoolName,
          prior_school_address: schoolAddress,
          prior_school_email: sendToSchool ? schoolEmail : null,
          grades_attended: grades,
          student_id_number: studentId,
          send_to_prior_school: sendToSchool,
        },
        { params: { token } },
      );
      setLastResult(data);
      toast.success("Records request emailed to you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      <Header
        title="Records Request"
        subtitle="Generate a formal letter requesting your child's records from their prior school."
      />
      <form onSubmit={submit} className="space-y-5" data-testid="records-request-form">
        <SectionCard title="Student">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              Which student?
            </Label>
            <Select value={studentIdx} onValueChange={setStudentIdx}>
              <SelectTrigger className="border-grey bg-white" data-testid="records-student-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {students.map((s, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {s.full_name} · {s.grade_level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4">
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              Grades attended at prior school
            </Label>
            <Input
              value={grades}
              onChange={(e) => setGrades(e.target.value)}
              required
              placeholder="e.g. Kindergarten – 3rd grade, 2022–2025"
              className="border-grey bg-white"
              data-testid="records-grades"
            />
          </div>
          <div className="mt-4">
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              Student ID number (optional)
            </Label>
            <Input
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              placeholder="If you know it"
              className="border-grey bg-white"
              data-testid="records-student-id"
            />
          </div>
        </SectionCard>

        <SectionCard title="Prior school">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              School name
            </Label>
            <Input
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              required
              placeholder="e.g. Berry Elementary School"
              className="border-grey bg-white"
              data-testid="records-school-name"
            />
          </div>
          <div className="mt-4">
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              School address
            </Label>
            <Textarea
              value={schoolAddress}
              onChange={(e) => setSchoolAddress(e.target.value)}
              required
              rows={3}
              placeholder="Street, City, State ZIP"
              className="border-grey bg-white"
              data-testid="records-school-address"
            />
          </div>
          <div className="mt-4 pt-4 border-t border-grey">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={sendToSchool}
                onCheckedChange={setSendToSchool}
                data-testid="records-send-to-school"
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-bold text-ink">Also email the letter directly to the prior school</div>
                <div className="text-xs text-ink/60 mt-0.5">
                  We&apos;ll send them a copy in addition to emailing it to you.
                </div>
              </div>
            </label>
            {sendToSchool && (
              <div className="mt-4">
                <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                  Prior school email
                </Label>
                <Input
                  type="email"
                  value={schoolEmail}
                  onChange={(e) => setSchoolEmail(e.target.value)}
                  required={sendToSchool}
                  placeholder="registrar@priorschool.org"
                  className="border-grey bg-white"
                  data-testid="records-school-email"
                />
              </div>
            )}
          </div>
        </SectionCard>

        <button
          type="submit"
          disabled={submitting}
          className="bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="records-submit-btn"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? "Generating..." : "Generate & email records request"}
        </button>

        {lastResult && (
          <div className="bg-emerald-50 border border-emerald-300 p-5 flex items-start gap-3" data-testid="records-result">
            <CheckCircle2 className="text-emerald-700 shrink-0 mt-0.5" size={18} />
            <div className="text-sm text-ink/85">
              <div className="font-bold text-emerald-900 mb-1">Letter generated.</div>
              <ul className="space-y-0.5 text-xs">
                <li>{lastResult.email_sent_to_parent ? "✓" : "✗"} PDF emailed to {me.email}</li>
                {sendToSchool && (
                  <li>{lastResult.email_sent_to_prior_school ? "✓" : "✗"} Copy sent to prior school</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

/* ---------------- Student Status ---------------- */
function StudentsTab({ me, token, onChange }) {
  const [saving, setSaving] = useState(null);

  const update = async (i, status) => {
    setSaving(i);
    try {
      await api.post(`/portal/students/${i}/status`, { status }, { params: { token } });
      toast.success("Student status updated.");
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      <Header title="Student Status" subtitle="Update each student's current standing at Zion Christian Academy." />
      <SectionCard title="Students">
        <ul className="divide-y divide-grey">
          {(me.students || []).map((s, i) => (
            <li key={i} className="py-4 flex items-center justify-between gap-4" data-testid={`student-row-${i}`}>
              <div>
                <div className="font-bold text-ink">{s.full_name}</div>
                <div className="text-xs text-ink/60">{s.grade_level} · DOB {s.date_of_birth}</div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={s.status || "Active"} onValueChange={(v) => update(i, v)}>
                  <SelectTrigger className="w-40 border-grey bg-white" data-testid={`student-status-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Withdrawn">Withdrawn</SelectItem>
                    <SelectItem value="Dismissed">Dismissed</SelectItem>
                    <SelectItem value="Graduated">Graduated</SelectItem>
                  </SelectContent>
                </Select>
                {saving === i && <Loader2 className="animate-spin text-purple" size={16} />}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink/60 mt-4 leading-relaxed">
          Changing a student&apos;s status here is a record-keeping action.
          If you plan to withdraw or graduate mid-year, please also email
          <a href={`mailto:${me?.settings?.contact_email || "zca.k.12.26@outlook.com"}`} className="text-purple hover:text-purple-dark ml-1">{me?.settings?.contact_email || "zca.k.12.26@outlook.com"}</a> so
          Director Brady can coordinate transcript and record delivery.
        </p>
      </SectionCard>
    </div>
  );
}

/* ---------------- Re-enrollment ---------------- */
function ReenrollTab({ me, token, onChange }) {
  const [years, setYears] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("cash_check_now");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const s = me.settings || {};
  const tierPrice = Number(s[`reenroll_${years}yr`] || 0);
  const activeStudents = (me.students || []).filter((st) => !["Withdrawn", "Dismissed", "Graduated"].includes(st.status));
  const total = tierPrice * activeStudents.length;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/reenroll", { years, payment_method: paymentMethod }, { params: { token } });
      setResult(data);
      toast.success("Re-enrolled for next school year.");
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-enrollment failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="p-8 max-w-2xl">
        <Header title="You're re-enrolled." subtitle={`Welcome back for ${result.school_year}.`} />
        <div className="bg-emerald-50 border border-emerald-300 p-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-emerald-700 mt-0.5" size={20} />
            <div className="text-sm">
              <div className="font-bold text-emerald-900 mb-1">Re-enrollment confirmed</div>
              <div className="space-y-1 text-ink/85">
                <div>New Enrollment ID: <span className="font-mono font-bold text-purple">{result.enrollment_id}</span></div>
                <div>School year: <b>{result.school_year}</b></div>
                <div>Students: <b>{result.students}</b></div>
                <div>Years committed: <b>{result.years_committed}</b></div>
                <div>Amount due: <b>${Number(result.amount_due).toFixed(2)}</b></div>
              </div>
              <p className="mt-4 text-xs text-ink/70 leading-relaxed">
                Payment instructions have been recorded. Director Brady will confirm
                receipt and unlock your new-year documents once payment arrives.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <Header title="Re-enrollment" subtitle="Commit to next school year and lock in a multi-year discount." />
      {activeStudents.length === 0 ? (
        <div className="border border-grey bg-white p-8 text-center text-ink/70 text-sm">
          You currently have no active students to re-enroll. Update student status first.
        </div>
      ) : (
        <>
          <SectionCard title="How many years?">
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5].map((y) => {
                const price = Number(s[`reenroll_${y}yr`] || 0);
                const active = y === years;
                const perYear = y > 0 ? price / y : 0;
                return (
                  <button
                    type="button"
                    key={y}
                    onClick={() => setYears(y)}
                    data-testid={`reenroll-tier-${y}`}
                    className={`border p-3 text-center transition-colors ${
                      active ? "border-purple bg-grey-light" : "border-grey bg-white hover:border-purple-light"
                    }`}
                  >
                    <div className="h-serif text-2xl text-purple-dark">{y}</div>
                    <div className="text-[10px] tracking-widest uppercase text-purple-light font-bold">
                      year{y === 1 ? "" : "s"}
                    </div>
                    <div className="text-xs text-ink/70 mt-2">${price.toFixed(0)} / student</div>
                    {y > 1 && <div className="text-[10px] text-emerald-700 mt-0.5">${perYear.toFixed(0)}/yr</div>}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-ink/60 mt-4">
              Longer commitments unlock a bigger per-year discount. You&apos;ll receive
              a new enrollment record and one payment for the entire tier.
            </p>
          </SectionCard>

          <SectionCard title="Payment method">
            <div className="space-y-2">
              <label className={`flex items-start gap-3 border p-4 cursor-pointer ${paymentMethod === "cash_check_now" ? "border-purple bg-grey-light" : "border-grey bg-white"}`}>
                <input type="radio" name="pm" checked={paymentMethod === "cash_check_now"} onChange={() => setPaymentMethod("cash_check_now")} data-testid="reenroll-cash" className="mt-1" />
                <div>
                  <div className="font-bold text-sm text-purple-dark">Cash or check now</div>
                  <div className="text-xs text-ink/70">Submit payment right away.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 border p-4 cursor-pointer ${paymentMethod === "payment_plan_end_of_year" ? "border-purple bg-grey-light" : "border-grey bg-white"}`}>
                <input type="radio" name="pm" checked={paymentMethod === "payment_plan_end_of_year"} onChange={() => setPaymentMethod("payment_plan_end_of_year")} data-testid="reenroll-plan" className="mt-1" />
                <div>
                  <div className="font-bold text-sm text-purple-dark">Payment plan</div>
                  <div className="text-xs text-ink/70">Contact the school to arrange a schedule.</div>
                </div>
              </label>
            </div>
          </SectionCard>

          <div className="bg-purple-dark text-white p-6 flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-widest uppercase text-gold-light font-bold mb-1">Total for {activeStudents.length} student{activeStudents.length === 1 ? "" : "s"} · {years}-year tier</div>
              <div className="h-serif text-3xl text-gold-light">${total.toFixed(2)}</div>
            </div>
            <button
              onClick={submit}
              disabled={submitting || total === 0}
              className="bg-gold text-ink hover:bg-gold-light font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="reenroll-submit-btn"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? "Submitting..." : "Confirm re-enrollment"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Transcript ---------------- */
function TranscriptTab({ me, token }) {
  const [studentIdx, setStudentIdx] = useState("0");
  const [deliveryEmail, setDeliveryEmail] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const fee = Number(me.settings?.transcript_fee || 0);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/portal/transcript-request",
        {
          student_index: Number(studentIdx),
          delivery_email: deliveryEmail,
          delivery_address: deliveryAddress,
          reason,
        },
        { params: { token } },
      );
      setResult(data);
      toast.success("Transcript request submitted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="p-8 max-w-2xl">
        <Header title="Transcript request received." subtitle={`Reference ID ${result.request_id}`} />
        <div className="bg-emerald-50 border border-emerald-300 p-6 text-sm text-ink/85">
          <CheckCircle2 className="text-emerald-700 mb-2" size={20} />
          Director Brady will review your request and email the transcript to
          <b className="mx-1">{deliveryEmail}</b> once the ${fee.toFixed(2)} fee is received.
          A confirmation email is on its way to you.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      <Header title="Transcript Request" subtitle={`Request an official transcript. Fee: $${fee.toFixed(2)}`} />
      <form onSubmit={submit} className="space-y-5" data-testid="transcript-form">
        <SectionCard title="Which student?">
          <Select value={studentIdx} onValueChange={setStudentIdx}>
            <SelectTrigger className="border-grey bg-white" data-testid="transcript-student">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(me.students || []).map((s, i) => (
                <SelectItem key={i} value={String(i)}>
                  {s.full_name} · {s.grade_level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SectionCard>
        <SectionCard title="Where should we send it?">
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Recipient email</Label>
              <Input type="email" value={deliveryEmail} onChange={(e) => setDeliveryEmail(e.target.value)} required placeholder="registrar@destination-school.org" className="border-grey" data-testid="transcript-email" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Recipient mailing address (optional)</Label>
              <Textarea rows={2} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} className="border-grey" data-testid="transcript-address" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">Reason for request (optional)</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. college application, transfer, homeschool records..." className="border-grey" data-testid="transcript-reason" />
            </div>
          </div>
        </SectionCard>
        <button
          type="submit"
          disabled={submitting}
          className="bg-purple text-white hover:bg-purple-dark font-bold px-8 py-3 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="transcript-submit"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? "Submitting..." : `Submit transcript request · $${fee.toFixed(2)}`}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Shared ---------------- */
function Header({ title, subtitle }) {
  return (
    <div className="mb-8">
      <h1 className="h-serif text-3xl text-purple-dark tracking-tight mb-1">{title}</h1>
      <p className="text-sm text-ink/70">{subtitle}</p>
    </div>
  );
}

function StatusCard({ label, value, Icon, accent, testid }) {
  const bg = accent === "emerald"
    ? "bg-emerald-50 border-emerald-200"
    : accent === "amber"
    ? "bg-amber-50 border-amber-200"
    : accent === "gold"
    ? "bg-gold/10 border-gold/30"
    : "bg-white border-grey";
  const iconClr = accent === "emerald"
    ? "text-emerald-700"
    : accent === "amber"
    ? "text-amber-700"
    : accent === "gold"
    ? "text-gold"
    : "text-purple-dark";
  return (
    <div className={`border p-5 ${bg}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-purple-light">{label}</div>
        {Icon && <Icon className={iconClr} size={18} />}
      </div>
      <div className="h-serif text-2xl text-purple-dark">{value}</div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-grey p-6 mb-4">
      <div className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-4">{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, Icon }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-purple-light mb-1 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="text-ink">{value}</div>
    </div>
  );
}
